/**
 * 見積り乖離判定に関するユーティリティ
 */
import { CellClassParams } from 'ag-grid-community'
import { colorPalette } from '../../../style/colorPalette'
import {
  ProductBacklogItemRow,
  ProductBacklogRow,
  isProductBacklogItemRow,
} from '../productBacklog'
import { WbsItemStatus } from '../../../../domain/entity/WbsItemEntity'

// 小数点誤差を考慮した閾値
const VARIANCE_THRESHOLD = 0.001

// スプリントごとの見積り工数情報
export type SprintEstimatedHourInfo = {
  sprintUuid: string
  sprintName: string
  estimatedHour: number
}

// 全スプリントの見積り時間合計を取得（sprintProductItemsベース）
const getTotalSprintEstimatedHour = (pbiRow: ProductBacklogItemRow): number => {
  const spis = pbiRow.sprintProductItems || []
  return spis.reduce((total, spi) => total + (spi.estimatedHour || 0), 0)
}

// 全スプリントのタスク見積り時間合計を取得（同一スプリントの重複を排除）
const getTotalSprintTaskEstimatedHour = (
  pbiRow: ProductBacklogItemRow
): number => {
  const spis = pbiRow.sprintProductItems || []
  // 同一スプリントに複数SPIがある場合、taskEstimatedHourは同値のため1つだけ採用する
  const hourBySprintUuid = new Map<string, number>()
  spis.forEach(spi => {
    if (!hourBySprintUuid.has(spi.sprintUuid)) {
      hourBySprintUuid.set(spi.sprintUuid, spi.taskEstimatedHour || 0)
    }
  })
  return Array.from(hourBySprintUuid.values()).reduce(
    (total, hour) => total + hour,
    0
  )
}

// スプリント別の見積り工数リストを取得（同一スプリントは合算、ただしdeduplicateオプションで重複排除可能）
const getSprintHourList = (
  pbiRow: ProductBacklogItemRow,
  hourExtractor: (
    spi: ProductBacklogItemRow['sprintProductItems'][number]
  ) => number,
  deduplicate = false
): SprintEstimatedHourInfo[] => {
  const spis = pbiRow.sprintProductItems || []
  const sprints = pbiRow.wbsItem.sprints || []

  // スプリントUUIDごとに工数を集計
  const hourBySprintUuid = new Map<string, number>()
  spis.forEach(spi => {
    if (deduplicate) {
      // 同一スプリントに複数SPIがある場合、値は同一のため1つだけ採用する
      if (!hourBySprintUuid.has(spi.sprintUuid)) {
        hourBySprintUuid.set(spi.sprintUuid, hourExtractor(spi))
      }
    } else {
      const current = hourBySprintUuid.get(spi.sprintUuid) || 0
      hourBySprintUuid.set(spi.sprintUuid, current + hourExtractor(spi))
    }
  })

  // スプリント情報を取得してリストを作成（重複排除済み）
  const result: SprintEstimatedHourInfo[] = []
  const processedSprintUuids = new Set<string>()

  sprints.forEach(sprint => {
    if (!sprint) return
    if (processedSprintUuids.has(sprint.uuid)) return
    processedSprintUuids.add(sprint.uuid)

    const totalHour = hourBySprintUuid.get(sprint.uuid) || 0
    result.push({
      sprintUuid: sprint.uuid,
      sprintName: sprint.name,
      estimatedHour: totalHour,
    })
  })

  return result
}

// スプリント別の成果物見積り工数リスト
export const getSprintEstimatedHourList = (
  pbiRow: ProductBacklogItemRow
): SprintEstimatedHourInfo[] =>
  getSprintHourList(pbiRow, spi => spi.estimatedHour || 0)

// スプリント別のタスク見積り工数リスト（同一スプリントの重複排除）
export const getSprintTaskEstimatedHourList = (
  pbiRow: ProductBacklogItemRow
): SprintEstimatedHourInfo[] =>
  getSprintHourList(pbiRow, spi => spi.taskEstimatedHour || 0, true)

// ============================================================================
// チェック1: 成果物 vs 成果物（スプリント）合計
// ============================================================================

export const getVarianceInfo = (pbiRow: ProductBacklogItemRow) => {
  const originalHour = pbiRow.wbsItem.estimatedWorkload?.hour ?? 0
  const sprintTotalHour = getTotalSprintEstimatedHour(pbiRow)
  const hasVariance =
    Math.abs(originalHour - sprintTotalHour) > VARIANCE_THRESHOLD
  return { originalHour, sprintTotalHour, hasVariance }
}

// 乖離検知を無効にするステータス
const VARIANCE_DISABLED_STATUSES = new Set([
  WbsItemStatus.DONE,
  WbsItemStatus.DISCARD,
])

// 乖離判定対象の行データを取得（対象外ならnull）
export const getVarianceTargetRow = (
  data: ProductBacklogRow | undefined
): ProductBacklogItemRow | null => {
  if (!data || !isProductBacklogItemRow(data)) return null
  if (
    data.wbsItem.status &&
    VARIANCE_DISABLED_STATUSES.has(data.wbsItem.status)
  ) {
    return null
  }
  const parentSprintUuid = data.treeValue?.[0]
  if (!parentSprintUuid) return null
  return data
}

// ============================================================================
// チェック2: 成果物 vs タスク合計
// ============================================================================

export const getTotalTaskVarianceInfo = (pbiRow: ProductBacklogItemRow) => {
  const deliverableHour = pbiRow.wbsItem.estimatedWorkload?.hour ?? 0
  const taskHour = pbiRow.cumulation
    ? pbiRow.cumulation.sumTaskEstimatedHour -
      pbiRow.cumulation.sumTaskEstimatedHourDiscard
    : 0
  const hasVariance = Math.abs(deliverableHour - taskHour) > VARIANCE_THRESHOLD
  return { deliverableHour, taskHour, hasVariance }
}

// ============================================================================
// チェック3: タスク vs タスク（スプリント）合計
// ============================================================================

export const getTaskTotalVsSprintVarianceInfo = (
  pbiRow: ProductBacklogItemRow
) => {
  const taskHour = pbiRow.cumulation
    ? pbiRow.cumulation.sumTaskEstimatedHour -
      pbiRow.cumulation.sumTaskEstimatedHourDiscard
    : 0
  const sprintTotalHour = getTotalSprintTaskEstimatedHour(pbiRow)
  const hasVariance = Math.abs(taskHour - sprintTotalHour) > VARIANCE_THRESHOLD
  return { taskHour, sprintTotalHour, hasVariance }
}

// ============================================================================
// チェック4: 成果物（スプリント） vs タスク（スプリント）（行単位）
// ============================================================================

// PBI行からSprintProductItemを検索する（行UUID優先、sprintUuidフォールバック）
export const findSprintProductItem = (
  pbiRow: ProductBacklogItemRow,
  sprintUuid: string
) => {
  return (
    pbiRow.sprintProductItems?.find(spi => spi.uuid === pbiRow.uuid) ??
    pbiRow.sprintProductItems?.find(spi => spi.sprintUuid === sprintUuid)
  )
}

// 行のSprintProductItemを取得
const getRowSprintProductItem = (pbiRow: ProductBacklogItemRow) => {
  const parentSprintUuid = pbiRow.treeValue?.[0]
  if (!parentSprintUuid) return undefined
  return findSprintProductItem(pbiRow, parentSprintUuid)
}

export const getTaskVarianceInfo = (pbiRow: ProductBacklogItemRow) => {
  const spi = getRowSprintProductItem(pbiRow)
  if (!spi) return { deliverableHour: 0, taskHour: 0, hasVariance: false }
  const spis = pbiRow.sprintProductItems || []
  // 同一スプリント内の成果物（スプリント）合計
  const deliverableHour = spis
    .filter(s => s.sprintUuid === spi.sprintUuid)
    .reduce((total, s) => total + (s.estimatedHour || 0), 0)
  const taskHour = spi.taskEstimatedHour || 0
  const hasVariance = Math.abs(deliverableHour - taskHour) > VARIANCE_THRESHOLD
  return { deliverableHour, taskHour, hasVariance }
}

// ============================================================================
// 乖離背景色スタイル（共通ファクトリ）
// ============================================================================

const createVarianceCellStyle =
  (checkVariance: (row: ProductBacklogItemRow) => boolean) =>
  (
    params: CellClassParams<ProductBacklogRow>
  ): { background: string } | undefined => {
    const row = getVarianceTargetRow(params.data)
    if (!row) return undefined
    if (checkVariance(row)) return { background: colorPalette.pink[1] }
    return undefined
  }

// 成果物列用: チェック1 or チェック2
export const getDeliverableTotalVarianceCellStyle = createVarianceCellStyle(
  row =>
    getVarianceInfo(row).hasVariance ||
    getTotalTaskVarianceInfo(row).hasVariance
)
// 成果物（スプリント）列用: チェック1 or チェック4
export const getDeliverableSprintVarianceCellStyle = createVarianceCellStyle(
  row =>
    getVarianceInfo(row).hasVariance || getTaskVarianceInfo(row).hasVariance
)
// タスク列用: チェック2 or チェック3
export const getTotalTaskVarianceCellStyle = createVarianceCellStyle(
  row =>
    getTotalTaskVarianceInfo(row).hasVariance ||
    getTaskTotalVsSprintVarianceInfo(row).hasVariance
)
// タスク（スプリント）列用: チェック3 or チェック4
export const getTaskVarianceCellStyle = createVarianceCellStyle(
  row =>
    getTaskTotalVsSprintVarianceInfo(row).hasVariance ||
    getTaskVarianceInfo(row).hasVariance
)
